import React, { useEffect, useRef, useState } from "react";
import "./imageWithLoader.css";

/**
 * Image that reserves space and shows a shimmer until fully loaded.
 * Drop-in replacement for <img> — pass through the same className/props.
 */
const ImageWithLoader = ({
  src,
  alt = "",
  className = "",
  style,
  onLoad,
  onError,
  minHeight,
  wrapperClassName = "",
  ...props
}) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!src) {
      setLoaded(true);
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const handleLoad = (event) => {
    setLoaded(true);
    onLoad?.(event);
  };

  const handleError = (event) => {
    setLoaded(true);
    onError?.(event);
  };

  const wrapperStyle = {
    ...(style?.maxWidth != null ? { maxWidth: style.maxWidth } : null),
    ...(style?.width != null ? { width: style.width } : null),
    ...(!loaded && minHeight != null ? { minHeight } : null),
    ...(!loaded && style?.maxHeight != null ? { maxHeight: style.maxHeight } : null),
  };

  return (
    <span
      className={`img-with-loader ${loaded ? "is-loaded" : "is-loading"} ${wrapperClassName}`.trim()}
      style={Object.keys(wrapperStyle).length ? wrapperStyle : undefined}
      aria-busy={!loaded}
    >
      {!loaded && (
        <span className="img-with-loader__placeholder" aria-hidden="true" />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`img-with-loader__img ${className}`.trim()}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </span>
  );
};

export default ImageWithLoader;
